import apiClient from '../../shared/lib/api'
import axios from 'axios'
import { getAuthToken } from '@trafiflow/shared-ui'

// Stream API client for long-running operations (no 29s API Gateway timeout)
const STREAM_API_URL = import.meta.env.VITE_STREAM_API_URL || 'https://gzgy25b7num4pzx3lgh3agdkta0siabt.lambda-url.us-east-1.on.aws'
const streamClient = axios.create({
  baseURL: STREAM_API_URL,
  timeout: 300000, // 5 minutes
  headers: { 'Content-Type': 'application/json' },
})
streamClient.interceptors.request.use(async (config) => {
  const token = await getAuthToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Storyboard types (matching backend AltairStoryboard)
export interface StoryboardScene {
  sceneNumber: number
  startTime: number
  endTime: number
  description: string
  visualType?: string
  audio?: string
  textOverlay?: string
  transitions?: string
  camera?: string
  mood?: string
  keyElements?: string[]
}

export interface StoryboardMeta {
  durationSec: number
  scenesCount: number
  suggestedHooks?: string[]
  brand?: string
  product?: string
}

export interface AltairStoryboard {
  scenes: StoryboardScene[]
  meta: StoryboardMeta
}

export interface RemixMatchRequest {
  storyboard: AltairStoryboard
  repositoryVideoIds: string[]
}

export interface SceneSuggestion {
  videoId: string
  videoTitle: string
  confidence: number
  reason: string
  suggestedTrimStart: number
  suggestedTrimEnd: number
}

export interface SceneMatch {
  sceneNumber: number
  sceneDuration: number
  suggestions: SceneSuggestion[]
}

export interface RemixMatchResponse {
  matches: SceneMatch[]
}

export interface RemixGenerateRequest {
  sourceStoryboard: AltairStoryboard
  sceneMatches: ConfirmedSceneMatch[]
  outputFormat?: string
}

export interface ConfirmedSceneMatch {
  sceneNumber: number
  videoId: string
  trimStart: number
  trimEnd: number
}

export interface RemixGenerateResponse {
  remixId: string
  status: string
  message?: string
  downloadUrl?: string
}

export interface RemixDownloadResponse {
  downloadUrl: string
  expiresIn: number
}

export async function matchScenesToFootage(
  storyboard: AltairStoryboard,
  repositoryVideoIds: string[]
): Promise<RemixMatchResponse> {
  // Use stream API to bypass API Gateway 29s timeout
  const res = await streamClient.post<RemixMatchResponse>('/api/remix/match', {
    storyboard,
    repositoryVideoIds,
  })
  return res.data
}

export async function generateRemix(
  sourceStoryboard: AltairStoryboard,
  sceneMatches: ConfirmedSceneMatch[],
  outputFormat = 'mp4'
): Promise<RemixGenerateResponse> {
  // Use stream API to bypass API Gateway 29s timeout (re-encoding takes time)
  const res = await streamClient.post<RemixGenerateResponse>('/api/remix/generate', {
    sourceStoryboard,
    sceneMatches,
    outputFormat,
  })
  return res.data
}

export async function getRemixDownload(remixId: string): Promise<RemixDownloadResponse> {
  const res = await apiClient.get<RemixDownloadResponse>(`/api/remix/${remixId}/download`)
  return res.data
}
